import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { X, Maximize2, Aperture, MapPin, Calendar, Layers, Droplet, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
type ImageFormat = "35mm" | "Medium Format";
type ImageType = "Monochrome" | "Color";
interface GalleryItem {
  id: string;
  src: string;
  title: string;
  type: ImageType;
  format: ImageFormat;
  date: string;
  location: string;
  film: string;
  developer: string;
  paper: string;
  description: string;
}
const galleryItems: GalleryItem[] = [{
  id: "1",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/film_scan_mono_portrait_01.png",
  title: "Silent Observer",
  type: "Monochrome",
  format: "35mm",
  date: "Oct 12, 2023",
  location: "Kyoto, Japan",
  film: "Ilford HP5 Plus",
  developer: "Rodinal 1:50",
  paper: "Ilford Multigrade FB",
  description: "A quiet moment captured in the backstreets of Gion. The grain structure emphasizes the timeless quality of the architecture."
}, {
  id: "2",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/film_scan_color_street_02.png",
  title: "Neon Rain",
  type: "Color",
  format: "35mm",
  date: "Nov 05, 2023",
  location: "Shinjuku, Tokyo",
  film: "Cinestill 800T",
  developer: "C-41 Standard",
  paper: "Fujicolor Crystal Archive",
  description: "Halation effects on neon signs during a heavy downpour. The tungsten balance brings out the cool blues of the rain."
}, {
  id: "3",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/film_scan_mono_landscape_03.png",
  title: "Highland Mist",
  type: "Monochrome",
  format: "Medium Format",
  date: "Sep 22, 2023",
  location: "Isle of Skye, Scotland",
  film: "Kodak Tri-X 400",
  developer: "D-76",
  paper: "Fomatone MG Classic",
  description: "Long exposure capturing the movement of mist over the jagged peaks. Medium format depth renders the rock textures with incredible fidelity."
}, {
  id: "4",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/film_scan_color_portrait_04.png",
  title: "Golden Hour Study",
  type: "Color",
  format: "Medium Format",
  date: "Aug 14, 2023",
  location: "Tuscany, Italy",
  film: "Kodak Portra 400",
  developer: "C-41 Professional",
  paper: "Kodak Endura Premier",
  description: "Natural light portraiture utilizing the warm tones of late summer. The skin tones are rendered with the classic Portra warmth."
}, {
  id: "5",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/film_scan_mono_architecture_05.png",
  title: "Brutalist Angles",
  type: "Monochrome",
  format: "35mm",
  date: "Dec 01, 2023",
  location: "London, UK",
  film: "Kentmere Pan 400",
  developer: "Ilfotec DD-X",
  paper: "Ilford RC Pearl",
  description: "High contrast study of concrete geometry. Pushed two stops to enhance grain and contrast."
}, {
  id: "6",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/film_scan_color_landscape_06.png",
  title: "Desert Solitude",
  type: "Color",
  format: "Medium Format",
  date: "Jul 30, 2023",
  location: "Joshua Tree, USA",
  film: "Kodak Ektar 100",
  developer: "C-41 Standard",
  paper: "Fujiflex Crystal Archive",
  description: "Vivid saturation characteristic of Ektar film. The blue sky contrasts sharply with the ochre rocks."
}, {
  id: "7",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/film_scan_mono_street_07.png",
  title: "Subway Commute",
  type: "Monochrome",
  format: "35mm",
  date: "Jan 15, 2024",
  location: "New York City, USA",
  film: "Kodak T-Max 3200",
  developer: "T-Max Developer",
  paper: "Ilford Multigrade FB Warmtone",
  description: "Available light photography in the underground. The high speed film captures the grit and motion of the city."
}, {
  id: "8",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/film_scan_color_abstract_08.png",
  title: "Prism Light",
  type: "Color",
  format: "35mm",
  date: "Feb 10, 2024",
  location: "Studio, Berlin",
  film: "Lomography Color 400",
  developer: "C-41 Standard",
  paper: "Fujicolor Crystal Archive",
  description: "Experimental light refraction study. Vibrant color shifts and unexpected leaks create a surreal atmosphere."
}, {
  id: "9",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/film_scan_mono_street_london_09.png",
  title: "The Watchman",
  type: "Monochrome",
  format: "35mm",
  date: "Mar 15, 2024",
  location: "London, UK",
  film: "Ilford Delta 3200",
  developer: "Microphen",
  paper: "Ilford Multigrade RC",
  description: "A candid moment outside a tube station. Delta 3200 provides the necessary speed for low-light street work with a characteristic heavy grain."
}, {
  id: "10",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/film_scan_color_landscape_iceland_10.png",
  title: "Basalt Shore",
  type: "Color",
  format: "Medium Format",
  date: "Apr 02, 2024",
  location: "Vik, Iceland",
  film: "Fujifilm Pro 400H",
  developer: "C-41 Professional",
  paper: "Fujicolor Crystal Archive",
  description: "The soft pastel palette of Pro 400H against the stark black sand of Iceland. Medium format clarity captures every detail of the basalt columns."
}, {
  id: "11",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/film_scan_mono_portrait_paris_11.png",
  title: "Rue de Seine",
  type: "Monochrome",
  format: "Medium Format",
  date: "May 20, 2024",
  location: "Paris, France",
  film: "Kodak Tri-X 400",
  developer: "HC-110 Dilution B",
  paper: "Fomatone MG Classic",
  description: "Environmental portraiture in Saint-Germain-des-Prés. The shallow depth of field isolates the subject against the historic Parisian storefronts."
}, {
  id: "12",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/film_scan_color_abstract_tokyo_12.png",
  title: "Electric Pulse",
  type: "Color",
  format: "35mm",
  date: "Jun 12, 2024",
  location: "Akihabara, Tokyo",
  film: "Cinestill 800T",
  developer: "C-41 Standard",
  paper: "Kodak Endura Premier",
  description: "Intentional camera movement during a long exposure of neon signage. The tungsten-balanced film creates a deep cinematic blue hue."
}];
const filters = ["All", "Monochrome", "Color", "Medium Format", "35mm"];
export default function VisualTaxonomy() {
  const [activeFilter, setActiveFilter] = React.useState("All");
  const [selectedImage, setSelectedImage] = React.useState<GalleryItem | null>(null);
  const filteredImages = React.useMemo(() => {
    if (activeFilter === "All") return galleryItems;
    return galleryItems.filter(item => item.type === activeFilter || item.format === activeFilter);
  }, [activeFilter]);
  return <section id="visual-taxonomy" className="dark bg-background text-foreground py-12 md:py-24 min-h-screen flex flex-col">
      <div className="container mx-auto px-4 md:px-6 flex flex-col gap-12">
        
        {}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 border-b border-border pb-6">
          <div className="space-y-2">
            <h2 className="text-3xl md:text-4xl font-serif tracking-tight">The Vault</h2>
            <p className="text-muted-foreground font-mono text-sm uppercase tracking-widest">
              Archival Scans / Visual Taxonomy
            </p>
          </div>

          <ToggleGroup type="single" value={activeFilter} onValueChange={value => value && setActiveFilter(value)} className="flex flex-wrap justify-start gap-2">
            {filters.map((filter, i) => <ToggleGroupItem key={filter} value={filter} data-index={i} className={cn("font-mono text-xs uppercase tracking-wider border border-border rounded-none px-4 py-2 hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground transition-all", activeFilter === filter ? "border-primary" : "bg-transparent")}>
                [{filter}]
              </ToggleGroupItem>)}
          </ToggleGroup>
        </div>

        {}
        <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
          {filteredImages.map((item, i) => <div key={item.id} data-index={i} className="break-inside-avoid group relative cursor-pointer overflow-hidden" onClick={() => setSelectedImage(item)}>
              <div className="relative w-full">
                <img src={item.src} alt={item.title} className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105 grayscale group-hover:grayscale-0" loading="lazy" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                
                {}
                <div className="absolute bottom-0 left-0 w-full p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-t from-black/80 to-transparent">
                  <p className="text-white font-mono text-xs uppercase tracking-wider">
                    {item.title}
                  </p>
                  <p className="text-white/70 font-mono text-[10px] uppercase mt-1">
                    {item.format} 
                  </p>
                </div>
              </div>
            </div>)}
        </div>

        {}
        {filteredImages.length === 0 && <div className="py-20 text-center border border-dashed border-border">
            <p className="text-muted-foreground font-mono text-sm">No archives found for this category.</p>
            <Button variant="link" onClick={() => setActiveFilter("All")} className="mt-2 text-primary">
              Reset Filters
            </Button>
          </div>}

      </div>

      {}
      <Dialog open={!!selectedImage} onOpenChange={open => !open && setSelectedImage(null)}>
        <DialogContent className="max-w-[95vw] w-full h-[90vh] md:h-[85vh] p-0 gap-0 bg-background border-border rounded-none overflow-hidden flex flex-col md:flex-row">
          
          {}
          <div className="relative flex-1 bg-black/95 flex items-center justify-center overflow-hidden h-[50vh] md:h-full">
            {selectedImage && <img src={selectedImage.src} alt={selectedImage.title} className="max-w-full max-h-full object-contain" />}
          </div>

          {}
          <div className="w-full md:w-[400px] flex-shrink-0 border-l border-border bg-card flex flex-col h-[50vh] md:h-full">
            <DialogHeader className="p-6 border-b border-border">
              <div className="flex justify-between items-start">
                <div>
                  <DialogTitle className="text-2xl font-serif font-normal tracking-tight mb-2">
                    {selectedImage?.title}
                  </DialogTitle>
                  <DialogDescription className="font-mono text-xs uppercase tracking-widest text-primary">
                    {selectedImage?.id.padStart(3, '0')} 
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <ScrollArea className="flex-1">
              <div className="p-6 space-y-8">
                
                {}
                <div className="space-y-2">
                  <h4 className="text-xs font-mono uppercase text-muted-foreground flex items-center gap-2">
                    <ScrollText className="w-3 h-3" /> Notes
                  </h4>
                  <p className="text-sm leading-relaxed text-card-foreground/90">
                    {selectedImage?.description}
                  </p>
                </div>

                {}
                <div className="grid grid-cols-1 gap-6">
                  
                  <div className="space-y-1">
                    <h4 className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-2">
                      <SafeHydrate><Calendar className="w-3 h-3" /></SafeHydrate> Date
                    </h4>
                    <p className="text-sm font-medium">{selectedImage?.date}</p>
                  </div>

                  <div className="space-y-1">
                    <h4 className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-2">
                      <MapPin className="w-3 h-3" /> Location
                    </h4>
                    <p className="text-sm font-medium">{selectedImage?.location}</p>
                  </div>

                  <div className="h-px bg-border w-full my-2" />

                  <div className="space-y-1">
                    <h4 className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-2">
                      <Aperture className="w-3 h-3" /> Film Stock
                    </h4>
                    <p className="text-sm font-medium">{selectedImage?.film}</p>
                  </div>

                  <div className="space-y-1">
                    <h4 className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-2">
                      <Droplet className="w-3 h-3" /> Developer
                    </h4>
                    <p className="text-sm font-medium">{selectedImage?.developer}</p>
                  </div>

                  <div className="space-y-1">
                    <h4 className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-2">
                      <Layers className="w-3 h-3" /> Paper
                    </h4>
                    <p className="text-sm font-medium">{selectedImage?.paper}</p>
                  </div>

                  <div className="space-y-1">
                    <h4 className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-2">
                      <Maximize2 className="w-3 h-3" /> Format
                    </h4>
                    <p className="text-sm font-medium">{selectedImage?.format}</p>
                  </div>

                </div>
              </div>
            </ScrollArea>

            {}
            <div className="p-6 border-t border-border bg-muted/20">
              <Button className="w-full rounded-none font-mono text-xs uppercase tracking-wider" size="lg">
                Request Print
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>;
}